import type * as React from 'react'
import { useEffect, useLayoutEffect } from 'react'
import type { JSX } from 'react'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'

import { LazyMotion } from 'motion/react'
import type motionFeatures from '~publish/motionFeatures'

import { ApolloProvider } from '@apollo/client/react'
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6'

import {
  AnonymousFeaturesWrapper,
  FeaturesWrapper,
  useSplitEnabled,
} from '@buffer-mono/features'
import { PopcornProvider } from '@buffer-mono/popcorn'

import { AuthGate } from '~publish/components/AuthGate'
import { PublishSidebarProvider } from '~publish/components/MainSidebar'
import getErrorBoundary from '~publish/components/ErrorBoundary'
import { LiveCollaborationNotes } from '~publish/components/PostNotes/LiveCollaborationNotes'
import {
  PublicPublishLoading,
  PublicPublishShell,
} from '~publish/components/PublicPublishShell'
import { AccountContextProvider } from '~publish/contexts/AccountContext'
import { env } from '~publish/env'
import { setFeatureFlagState } from '~publish/helpers/apolloRetryControl'
import { isPublicPublishPath } from '~publish/helpers/publicPublishPaths'
import { client } from '~publish/legacy/apollo-client'
import { store } from '~publish/legacy/store'
import { PusherContextProvider } from '~publish/services/pusher'
import { RealTimeProvider } from '~publish/services/realtime'
import '~publish/legacy/composer/composer/components/css/constants.css'
import '~publish/legacy/global-styles/uploadcare-overrides.css'

import { markAppMounted } from '~publish/components/MountFailure'
import App from '../App'
import { MixpanelSessionReplay } from '../MixpanelSessionReplay/MixpanelSessionReplay'

/**
 * Feature Flag Sync Component
 *
 * Syncs the `apollo-retry-disabled` feature flag state with Apollo Client's
 * retry control system. This allows remote control of retry behavior during incidents.
 *
 * How it works:
 * - Split.io SDK automatically polls for flag updates every 60 seconds
 * - When a flag changes, useSplitEnabled hook triggers a re-render
 * - This component updates the module-level state in apolloRetryControl.ts
 * - Apollo retry logic uses that state synchronously during retry decisions
 *
 * Why this approach:
 * - Leverages Split.io's built-in polling (no custom polling needed)
 * - Minimal React involvement (just 10 lines to bridge React → module state)
 * - Split.io handles all caching, updates, and error handling
 * - Module-level state allows non-React code (Apollo links) to access it
 */
function ApolloRetryFeatureFlagSync(): null {
  const { isEnabled, isReady } = useSplitEnabled('apollo-retry-disabled')

  useEffect(() => {
    if (isReady) {
      setFeatureFlagState(isEnabled)
    }
  }, [isEnabled, isReady])

  return null
}

import '../../components/i18n'

function getQueryParams(): URLSearchParams {
  const url = new URL(window.location.href)
  const params = new URLSearchParams(url.search)
  return params
}

// Test-only escape hatch (CORE-7761): lets Playwright force Split flag
// values via the SDK's official offline/localhost mode (see
// `FeaturesWrapper`'s `mockSplits` prop), by setting this global with
// `page.addInitScript()` before navigation — never reachable via a query
// param or any other production-facing input. Undefined in every real
// session, since nothing else ever sets it.
declare global {
  interface Window {
    __E2E_MOCK_SPLITS__?: SplitIO.MockedFeaturesMap
  }
}

const ErrorBoundary = getErrorBoundary()

type MotionFeatures = typeof motionFeatures

const loadMotionFeatures = async (): Promise<MotionFeatures> =>
  import('~publish/motionFeatures').then(({ default: domMax }) => domMax)

function AppMountedMarker(): null {
  useLayoutEffect(() => {
    markAppMounted()
  }, [])

  return null
}

const Publish = (): JSX.Element => {
  useEffect(() => {
    store.dispatch({ type: 'APP_INIT', queryParams: getQueryParams() })
  }, [])

  return (
    <ErrorBoundary>
      <FeaturesWrapper
        environment={env.VITE_SPLIT_ENV}
        // eslint-disable-next-line no-underscore-dangle -- test-only global, see declaration above
        mockSplits={window.__E2E_MOCK_SPLITS__}
      >
        <ApolloRetryFeatureFlagSync />
        <MixpanelSessionReplay />
        <Provider store={store}>
          <AccountContextProvider dispatch={store.dispatch}>
            <BrowserRouter
              future={{
                v7_relativeSplatPath: true,
                v7_startTransition: true,
              }}
            >
              <NuqsAdapter>
                <PusherContextProvider>
                  <RealTimeProvider>
                    <PopcornProviderWrapper>
                      <LazyMotion features={loadMotionFeatures}>
                        <LiveCollaborationNotes />
                        <PublishSidebarProvider>
                          <App />
                        </PublishSidebarProvider>
                      </LazyMotion>
                    </PopcornProviderWrapper>
                  </RealTimeProvider>
                </PusherContextProvider>
              </NuqsAdapter>
            </BrowserRouter>
          </AccountContextProvider>
        </Provider>
      </FeaturesWrapper>
    </ErrorBoundary>
  )
}

/**
 * Boot tree for `/p/*` public pages. Deliberately excludes the authenticated
 * providers (Redux store, AccountContext, Pusher, session replay): logged-out
 * visitors render the public shell, and authenticated visitors leave this
 * branch through AuthGate's full-page navigation to the unprefixed app route.
 */
const PublicPublish = (): JSX.Element => {
  return (
    <ErrorBoundary>
      <AnonymousFeaturesWrapper>
        <ApolloRetryFeatureFlagSync />
        <BrowserRouter
          future={{
            v7_relativeSplatPath: true,
            v7_startTransition: true,
          }}
        >
          <NuqsAdapter>
            <PopcornProviderWrapper toaster forcedTheme="light">
              <LazyMotion features={loadMotionFeatures}>
                <AuthGate
                  loading={<PublicPublishLoading />}
                  loggedOut={<PublicPublishShell />}
                />
              </LazyMotion>
            </PopcornProviderWrapper>
          </NuqsAdapter>
        </BrowserRouter>
      </AnonymousFeaturesWrapper>
    </ErrorBoundary>
  )
}

export function PublishRoot(): JSX.Element {
  // Boot-time branch: `/p/*` is the logged-out public surface, everything else
  // boots the legacy authenticated flow untouched. Client-side navigation never
  // crosses the branch (public links stay under `/p`, the app never links into
  // it), so the decision is stable for the lifetime of the page.
  const isPublicPath = isPublicPublishPath(window.location.pathname)

  return (
    <ApolloProvider client={client}>
      <AppMountedMarker />
      {isPublicPath ? <PublicPublish /> : <Publish />}
    </ApolloProvider>
  )
}

function PopcornProviderWrapper({
  children,
  toaster = false,
  forcedTheme,
}: {
  children: React.ReactNode
  toaster?: boolean
  forcedTheme?: 'light'
}): JSX.Element {
  return (
    <PopcornProvider
      portalContainerId="portals-container"
      _toaster={toaster}
      forcedTheme={forcedTheme}
    >
      {children}
    </PopcornProvider>
  )
}
